import { useEffect, useState } from 'react';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DarkModeIcon from '@material-ui/icons/Brightness4';
import LightModeIcon from '@material-ui/icons/Brightness7';
import { manewcoTokens } from '@platform/plugin-platform-react';
import { MANEWCO_DARK_THEME_ID, MANEWCO_THEME_ID } from '../theme/themeIds';

const useStyles = makeStyles(theme => {
  const t = manewcoTokens(theme);
  return {
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      margin: '0 16px 4px',
      padding: '6px 10px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontFamily: t.fontFamily,
      fontSize: 12,
      color: t.navText,
      borderRadius: t.radius,
      '&:hover': { color: t.primary, backgroundColor: t.hover },
    },
  };
});

/**
 * Bascule clair / sombre du bas du menu, sous « Paramètres » (Manewco n'a pas de barre du haut).
 * L'état suit l'API des thèmes : un choix fait depuis la page Paramètres met le libellé à jour.
 * Sans choix Manewco (automatique ou autre thème), on lit le mode du thème effectivement rendu.
 */
export const ThemeToggle = () => {
  const classes = useStyles();
  const appThemeApi = useApi(appThemeApiRef);
  const { isOpen } = useSidebarOpenState();
  const renderedDark = useTheme().palette.type === 'dark';
  const [activeId, setActiveId] = useState<string | undefined>(() => appThemeApi.getActiveThemeId());

  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(id => setActiveId(id));
    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  // Thème Manewco choisi : il fait foi (il suit aussi un choix fait dans Paramètres). Tout autre
  // cas (automatique, ancien thème « light » / « dark » mémorisé…) : le thème réellement affiché.
  const dark =
    activeId === MANEWCO_DARK_THEME_ID ? true : activeId === MANEWCO_THEME_ID ? false : renderedDark;
  const label = dark ? 'Mode clair' : 'Mode sombre';
  const Icon = dark ? LightModeIcon : DarkModeIcon;
  const toggle = () => appThemeApi.setActiveThemeId(dark ? MANEWCO_THEME_ID : MANEWCO_DARK_THEME_ID);

  if (!isOpen) return <SidebarItem icon={Icon} text={label} onClick={toggle} />;
  return (
    <button type="button" className={classes.button} onClick={toggle}>
      <Icon fontSize="small" aria-hidden />
      {label}
    </button>
  );
};
